import React, { useState, useEffect, useCallback } from 'react';
import { auditApi } from '../services/api';
import { useProject } from '../App';
import { formatTimestamp, formatNumber } from '../utils/format';

function Dashboard() {
    const { selectedProject } = useProject();
    const [stats, setStats] = useState(null);
    const [recentTransactions, setRecentTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const [statsRes, txRes] = await Promise.all([
                auditApi.getStats(selectedProject),
                auditApi.queryTransactions({ projectId: selectedProject, page: 0, size: 10 }),
            ]);
            setStats(statsRes.data);
            setRecentTransactions(txRes.data.content || []);
            setError(null);
        } catch (err) {
            setError('Failed to load dashboard data. Make sure the backend is running.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [selectedProject]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-xl text-gray-500">Loading dashboard...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <div className="text-red-600 text-lg font-medium">⚠️ Connection Error</div>
                <div className="text-red-500 mt-2">{error}</div>
                <button
                    onClick={loadData}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-900">Audit Dashboard</h1>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <StatCard
                    title="Total Transactions"
                    value={stats?.totalTransactions || 0}
                    icon="📊"
                    color="blue"
                />
                <StatCard
                    title="Total Changes"
                    value={stats?.totalChanges || 0}
                    icon="📝"
                    color="green"
                />
                <StatCard
                    title="GUI Operations"
                    value={stats?.guiTransactions || 0}
                    icon="🖥️"
                    color="purple"
                />
                <StatCard
                    title="SQL Direct"
                    value={stats?.sqlDirectTransactions || 0}
                    icon="💾"
                    color="orange"
                />
            </div>

            {/* Recent Transactions */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-medium text-gray-900">Recent Transactions</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Function</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tables</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Changes</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {recentTransactions.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                                        No audit transactions found. Run some test SQL scenarios to see data here.
                                    </td>
                                </tr>
                            ) : (
                                recentTransactions.map((tx) => (
                                    <tr key={tx.transactionId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <a
                                                href={`/transactions/${encodeURIComponent(tx.transactionId)}`}
                                                className="text-blue-600 hover:text-blue-800 font-mono text-sm"
                                            >
                                                {tx.transactionId?.substring(0, 40)}...
                                            </a>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                tx.source === 'GUI'
                                                    ? 'bg-purple-100 text-purple-800'
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {tx.source || 'UNKNOWN'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {tx.guiFunctionName || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {tx.totalTableChanges || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {tx.totalFieldChanges || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatTimestamp(tx.commitTimestamp)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function StatCard({ title, value, icon, color }) {
    const colorClasses = {
        blue: 'bg-blue-50 border-blue-200',
        green: 'bg-green-50 border-green-200',
        purple: 'bg-purple-50 border-purple-200',
        orange: 'bg-orange-50 border-orange-200',
    };

    return (
        <div className={`border rounded-lg p-6 ${colorClasses[color]}`}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">{title}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{formatNumber(value)}</p>
                </div>
                <div className="text-4xl">{icon}</div>
            </div>
        </div>
    );
}

export default Dashboard;
